/**
 * Google Visibility is a Surface Renaud Inc internal tracking dashboard.
 * It is not a global CrewPilot feature.
 *
 * Authority is the *active* organization on the actor (organization_members),
 * compared to the canonical Surface Renaud organization id, plus owner/admin
 * membership. Privileged roles in other organizations do not grant access.
 */

import { isGuestActor } from "./guestMode";
import { MEMBERSHIP_ROLE } from "./membershipHelpers";
import { isAdmin } from "./permissions";

/** Canonical Surface Renaud Inc organization id (customer tenant). */
export const SURFACE_RENAUD_ORGANIZATION_ID =
  "e5aadc71-3b57-4ecd-b28a-81bf47a9bc13";

export const GOOGLE_VISIBILITY_SCREEN = "google_visibility";
export const GOOGLE_VISIBILITY_HASH = "google-visibility";

export function isSurfaceRenaudActiveOrganization(actor) {
  return (
    Boolean(actor?.organization_id) &&
    actor.organization_id === SURFACE_RENAUD_ORGANIZATION_ID
  );
}

/**
 * Owner/admin from organization_members. Enriched actors expose
 * membership_role (SoT) and map owner/admin → legacy role "admin".
 */
export function actorIsOrganizationOwnerOrAdmin(actor) {
  if (!actor || isGuestActor(actor)) {
    return false;
  }

  const membershipRole = actor.membership_role;
  if (
    membershipRole === MEMBERSHIP_ROLE.OWNER ||
    membershipRole === MEMBERSHIP_ROLE.ADMIN
  ) {
    return true;
  }

  return isAdmin(actor);
}

/**
 * UI + route gate. Active org must be Surface Renaud and the actor must be
 * an owner/admin of that membership. Employees and other orgs are denied.
 */
export function actorCanAccessGoogleVisibility(actor) {
  if (!actor || isGuestActor(actor)) {
    return false;
  }
  return (
    isSurfaceRenaudActiveOrganization(actor) &&
    actorIsOrganizationOwnerOrAdmin(actor)
  );
}

/**
 * Membership org/role may arrive after the first authenticated paint.
 * Do not deny the inbound hash until the actor can be judged.
 */
export function isGoogleVisibilityActorReady(actor) {
  if (!actor || isGuestActor(actor)) {
    return true;
  }
  return Boolean(actor.organization_id || actor.membership_role);
}

export const GOOGLE_VISIBILITY_ROUTE_DECISION = Object.freeze({
  IGNORE: "ignore",
  WAIT: "wait",
  ALLOW: "allow",
  DENY: "deny",
});

/**
 * Decide what a direct/cold-load #google-visibility route should do.
 * routeActive is the inbound hash/query, including a boot snapshot.
 */
export function resolveGoogleVisibilityRouteDecision(actor, routeActive) {
  if (!routeActive) {
    return GOOGLE_VISIBILITY_ROUTE_DECISION.IGNORE;
  }
  if (!actor) {
    return GOOGLE_VISIBILITY_ROUTE_DECISION.WAIT;
  }
  if (!isGoogleVisibilityActorReady(actor)) {
    return GOOGLE_VISIBILITY_ROUTE_DECISION.WAIT;
  }
  if (actorCanAccessGoogleVisibility(actor)) {
    return GOOGLE_VISIBILITY_ROUTE_DECISION.ALLOW;
  }
  return GOOGLE_VISIBILITY_ROUTE_DECISION.DENY;
}

function normalizeHash(hash) {
  return String(hash || "")
    .replace(/^#\/?/, "")
    .split("?")[0]
    .trim();
}

export function isGoogleVisibilityRoute(location) {
  const loc =
    location || (typeof window !== "undefined" ? window.location : null);
  if (!loc) {
    return false;
  }
  if (normalizeHash(loc.hash) === GOOGLE_VISIBILITY_HASH) {
    return true;
  }
  try {
    const search = loc.search || "";
    return (
      new URLSearchParams(search).get("screen") === GOOGLE_VISIBILITY_SCREEN
    );
  } catch {
    return false;
  }
}

export function setGoogleVisibilityRoute(active, location) {
  if (typeof window === "undefined") {
    return;
  }
  const loc = location || window.location;
  const currentHash = normalizeHash(loc.hash);
  if (active) {
    if (currentHash === GOOGLE_VISIBILITY_HASH) {
      return;
    }
    const url = `${loc.pathname}${loc.search}#${GOOGLE_VISIBILITY_HASH}`;
    window.history.replaceState(null, "", url);
    return;
  }
  if (currentHash !== GOOGLE_VISIBILITY_HASH) {
    return;
  }
  window.history.replaceState(null, "", `${loc.pathname}${loc.search}`);
}
